import { SendTradeInfoRequest } from '@optimex-xyz/trade-wallet-sdk'

import { IResponse, Model, ObjectsFactory } from '@/data'
import { GET_INDICATIVE_QUOTE_ENDPOINT, GET_SLIPPAGE, SEND_TRADE_INFO } from '@/data/endpoint'
import { TradeType } from '@/types'
import { API_ENDPOINT } from '@/utils/constants'

export interface IndicativeQuoteRequest {
  fromTokenId: string
  toTokenId: string
  fromTokenAmount: string
  affiliateFeeBps: string
  debug: boolean
  sessionId?: string
  fromUserAddress?: string
  userReceivingAddress?: string
  userRefundPubkey?: string
}

interface PmmQuote {
  pmmId: string
  pmmReceivingAddress: string
}

export interface IIndicativeQuote {
  solverAddress: string
  sessionId: string
  bestQuoteAfterFees: string
  protocolFee: number
  pmmFinalists: PmmQuote[]
  bestQuoteAfterFeesAfterBridge: string
}

export interface SendTradeParams {
  request: SendTradeInfoRequest
  privateKey: string
}

interface UpdateTradeInfoRequest {
  tradeId: string
  minAmountOut: string
  sessionDeadline: number
  signature: string
}

export interface UpdateTradeParams {
  request: UpdateTradeInfoRequest
  privateKey: string
}

export interface SuggestSlippageRequest {
  fromTokenId: string
  toTokenId?: string
  fromTokenAmount: string
  type: TradeType
}

export interface SuggestSlippageResponse {
  autoSlippage: number | null
  maxSlippage: number | null
  autoSlippageBridge: number | null
}

export class Optimex extends Model {
  static config = { endpoint: GET_INDICATIVE_QUOTE_ENDPOINT, baseUrl: API_ENDPOINT }

  static objects = ObjectsFactory.factory<void>(Optimex.config)

  static async getIndicativeQuote(request: IndicativeQuoteRequest) {
    return await this.service.post<IResponse<IIndicativeQuote>>({
      url: GET_INDICATIVE_QUOTE_ENDPOINT,
      payload: request,
    })
  }

  static async sendTradeInfo({ request }: SendTradeParams) {
    return await this.service.post({
      url: SEND_TRADE_INFO,
      payload: request,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    })
  }

  static async updateTradeInfo({ request }: UpdateTradeParams) {
    return await this.service.put({
      url: `${SEND_TRADE_INFO}/${request.tradeId}`,
      payload: request,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    })
  }

  static async getSuggestSlippage(request: SuggestSlippageRequest): Promise<SuggestSlippageResponse> {
    const { data } = await this.service.post<IResponse<{ bps: string; maxBps: string; bridgeBps: string }>>({
      url: GET_SLIPPAGE,
      payload: request,
    })
    const autoSlippage = data?.bps ? +data?.bps / 100 : null
    const maxSlippage = data?.maxBps ? +data?.maxBps / 100 : null
    const autoSlippageBridge = data?.bridgeBps ? +data?.bridgeBps / 100 : null
    return { autoSlippage, maxSlippage, autoSlippageBridge }
  }
}

Optimex.init(Optimex.config)
